import { useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, KeyRound, Plus } from "lucide-react";
import { API } from "../constants";

/**
 * Self-service device-limit increase.
 *
 * Fires when /activate returns 409 { error: 'device_limit_reached', ... }.
 * Walks the manager through 3 phases:
 *   1. start   — pings the hotel manager email with a 6-digit OTP
 *   2. otp     — types the OTP; we get a limit_token + list of codes/caps
 *   3. pick    — pick a code + new limit → PATCH; on success caller can retry
 *
 * Never touches credits or billing; capacity only.
 */
export default function RequestMoreDevicesModal({ code, sector, onClose, onSuccess }) {
  const [phase, setPhase] = useState("start"); // start | otp | pick | done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [requestId, setRequestId] = useState("");
  const [emailMask, setEmailMask] = useState("");
  const [otp, setOtp] = useState("");
  const [limitToken, setLimitToken] = useState("");
  const [codes, setCodes] = useState([]);
  const [pickedCodeId, setPickedCodeId] = useState("");
  const [newLimit, setNewLimit] = useState(3);

  const orgLabel = sector === "school" ? "school" : sector === "charity" ? "organisation" : "hotel";
  const managerLabel = sector === "school" ? "school office" : sector === "charity" ? "organisation office" : "hotel manager";

  const doStart = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/hotel/device-limit/request-start`, { activation_code: code });
      setRequestId(r.data.request_id);
      setEmailMask(r.data.manager_email_masked || "");
      setPhase("otp");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sorry, we couldn't start the request. Please try again.");
    } finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/hotel/device-limit/request-confirm`, { request_id: requestId, otp: otp.trim() });
      setLimitToken(r.data.limit_token);
      setCodes(r.data.codes || []);
      const target = (r.data.codes || []).find(c => c.code === code) || (r.data.codes || [])[0];
      if (target) {
        setPickedCodeId(target.code_id);
        setNewLimit(Math.min(target.max_allowed || 25, Math.max((target.expected_devices || 1) + 2, target.active_devices + 1)));
      }
      setPhase("pick");
    } catch (e) {
      setErr(e?.response?.data?.detail || "That code isn't correct. Please try again.");
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/hotel/device-limit/request-apply`, {
        limit_token: limitToken, code_id: pickedCodeId, new_limit: newLimit,
      });
      setPhase("done");
      onSuccess && onSuccess(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sorry, we couldn't apply the change. Please try again.");
    } finally { setBusy(false); }
  };

  const picked = codes.find(c => c.code_id === pickedCodeId);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="request-more-devices-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 space-y-4 max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="text-xs text-[#9A9A9A] hover:text-[#4A4A4A]" data-testid="rmd-close">← Cancel</button>

        {phase === "start" && (
          <>
            <div className="text-center">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-[#B89823]" />
              <h3 className="text-xl font-bold text-[#4A4A4A]" data-testid="rmd-title">Request more devices</h3>
              <p className="text-sm text-[#6B6B6B] mt-2">
                To keep your {orgLabel} secure, we'll send a 6-digit code to the {managerLabel} email on file. Only the manager can raise the device limit — having the activation code alone isn't enough.
              </p>
            </div>
            {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center">{err}</div>}
            <button onClick={doStart} disabled={busy} data-testid="rmd-send-otp" className="w-full px-4 py-3 bg-[#4A4A4A] text-white rounded-full font-semibold hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {busy ? "Sending…" : "Send code to manager"}
            </button>
          </>
        )}

        {phase === "otp" && (
          <>
            <div className="text-center">
              <KeyRound className="w-10 h-10 mx-auto mb-3 text-[#B89823]" />
              <h3 className="text-xl font-bold text-[#4A4A4A]">Enter the 6-digit code</h3>
              <p className="text-sm text-[#6B6B6B] mt-2">We just emailed <strong>{emailMask}</strong>. The code expires in 10 minutes.</p>
            </div>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              placeholder="000000"
              autoFocus
              inputMode="numeric"
              data-testid="rmd-otp-input"
              className="w-full px-4 py-3 rounded-2xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-center font-mono text-2xl tracking-[0.5em]"
            />
            {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center" data-testid="rmd-otp-error">{err}</div>}
            <button onClick={doConfirm} disabled={busy || otp.length !== 6} data-testid="rmd-confirm-otp" className="w-full px-4 py-3 bg-[#4A4A4A] text-white rounded-full font-semibold hover:bg-black disabled:opacity-50">
              {busy ? "Verifying…" : "Verify"}
            </button>
          </>
        )}

        {phase === "pick" && (
          <>
            <div>
              <h3 className="text-lg font-bold text-[#4A4A4A] text-center">Which code needs more devices?</h3>
              <p className="text-xs text-[#9A9A9A] text-center mt-1">All codes share the same central credit balance. Raising the limit adds capacity only — no new credits or payments.</p>
            </div>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="rmd-codes-list">
              {codes.map(c => (
                <label key={c.code_id} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${pickedCodeId === c.code_id ? "border-[#B89823] bg-[#FFF7D6]" : "border-[#E0E0E0] hover:border-[#D0D0D0]"}`} data-testid={`rmd-code-${c.code}`}>
                  <input type="radio" name="code" checked={pickedCodeId === c.code_id} onChange={() => { setPickedCodeId(c.code_id); setNewLimit(Math.max((c.expected_devices || 1) + 2, c.active_devices + 1)); }} className="mt-1 accent-[#B89823]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-semibold text-sm text-[#4A4A4A] truncate">{c.code}</p>
                    <p className="text-[11px] text-[#6B6B6B] truncate">{c.venue_label}</p>
                    <p className="text-[11px] text-[#4A4A4A]"><strong>{c.active_devices}</strong> active / <strong>{c.expected_devices}</strong> allowed</p>
                  </div>
                </label>
              ))}
            </div>
            {picked && (
              <div className="pt-2">
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">New device limit for {picked.code}</label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setNewLimit(Math.max(picked.active_devices, newLimit - 1))} className="px-3 py-2 rounded-full bg-[#F3F0E6] hover:bg-[#E8E5DC]">−</button>
                  <div className="flex-1 text-center font-bold text-2xl text-[#4A4A4A]" data-testid="rmd-new-limit">{newLimit}</div>
                  <button type="button" onClick={() => setNewLimit(Math.min(picked.max_allowed || 25, newLimit + 1))} className="px-3 py-2 rounded-full bg-[#F3F0E6] hover:bg-[#E8E5DC]">+</button>
                </div>
                <p className="text-[11px] text-[#9A9A9A] text-center mt-1">Currently {picked.expected_devices} allowed. Max {picked.max_allowed || 25}.</p>
              </div>
            )}
            {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center">{err}</div>}
            <button onClick={doApply} disabled={busy || !picked || newLimit <= (picked?.expected_devices || 1)} data-testid="rmd-apply" className="w-full px-4 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {busy ? "Updating…" : "Update device limit"}
            </button>
          </>
        )}

        {phase === "done" && (
          <>
            <div className="text-center">
              <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-[#7A9A7A]" />
              <h3 className="text-xl font-bold text-[#4A4A4A]" data-testid="rmd-done-title">Device limit updated</h3>
              <p className="text-sm text-[#6B6B6B] mt-2">You can now activate this device. A confirmation email has been sent to {emailMask}.</p>
            </div>
            <button onClick={onClose} data-testid="rmd-done-close" className="w-full px-4 py-3 bg-[#4A4A4A] text-white rounded-full font-semibold hover:bg-black">
              Retry activation
            </button>
          </>
        )}
      </div>
    </div>
  );
}
